import { useState, useEffect, useRef } from 'react'
import { X, Plus, Search, Trash2, Loader2, Send, Package, Truck, AlertCircle } from 'lucide-react'
import api from '@/lib/api'

interface QuoteModalProps {
  repairId: number
  repair: any
  onClose: () => void
  onSent: () => void
}

interface QuoteItem {
  item_type: string
  factusol_reference?: string
  factusol_family?: string
  supplier_name?: string
  supplier_reference?: string
  description: string
  quantity: number
  cost_price: number
  sale_price: number
}

interface Config {
  hourly_cost_rate: number
  margin_pvp: number
  margin_pvd: number
  margin_vip: number
  vat_rate: number
  pickup_shipping_cost: number
  baleares_shipping_cost: number
}

interface SearchResult {
  reference: string
  name: string
  family: string
  cost_price: number
  pvp_price: number
  stock: number
}

export default function QuoteModal({ repairId, repair, onClose, onSent }: QuoteModalProps) {
  const [items, setItems] = useState<QuoteItem[]>([])
  const [config, setConfig] = useState<Config | null>(null)
  const [quoteNotes, setQuoteNotes] = useState('')
  const [sending, setSending] = useState(false)

  // Search
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadConfig()
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadConfig = async () => {
    try {
      const res = await api.get('/repairs/admin/config')
      setConfig(res.data)
    } catch (err) {
      console.error('Error loading config:', err)
    }
  }

  const searchParts = async () => {
    if (searchQuery.length < 2) return
    setSearching(true)
    try {
      const res = await api.get(`/repairs/admin/search-parts?q=${encodeURIComponent(searchQuery)}`)
      setSearchResults(res.data)
      setShowResults(true)
    } catch (err) {
      console.error('Error searching parts:', err)
    } finally {
      setSearching(false)
    }
  }

  const addCatalogPart = (part: SearchResult) => {
    setItems([...items, {
      item_type: 'catalog_part',
      factusol_reference: part.reference,
      factusol_family: part.family,
      description: part.name,
      quantity: 1,
      cost_price: part.cost_price || 0,
      sale_price: part.pvp_price || 0,
    }])
    setShowResults(false)
    setSearchQuery('')
    setSearchResults([])
  }

  const addCustomPart = () => {
    setItems([...items, {
      item_type: 'custom_part',
      description: '',
      supplier_name: '',
      supplier_reference: '',
      quantity: 1,
      cost_price: 0,
      sale_price: 0,
    }])
  }

  const addShipping = () => {
    const isBalears = repair.pickup_address?.postal_code?.startsWith('07')
    const cost = isBalears ? (config?.baleares_shipping_cost || 15.90) : (config?.pickup_shipping_cost || 9.90)
    const shippingCost = cost * 2 // ida + vuelta
    setItems([...items, {
      item_type: 'shipping',
      description: `Recogida + devolución (${isBalears ? 'Baleares' : 'Península'})`,
      quantity: 1,
      cost_price: shippingCost,
      sale_price: shippingCost,
    }])
  }

  const updateItem = (index: number, field: string, value: any) => {
    const updated = [...items]
    ;(updated[index] as any)[field] = value
    setItems(updated)
  }

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
  }

  // Calculate totals
  const totalCost = items.reduce((sum, i) => sum + i.cost_price * i.quantity, 0)
  const totalNet = items.reduce((sum, i) => sum + i.sale_price * i.quantity, 0)
  const marginAmount = totalNet - totalCost
  const marginPct = totalCost > 0 ? ((marginAmount / totalCost) * 100) : 0
  const vatRate = config?.vat_rate || 21
  const vatAmount = totalNet * (vatRate / 100)
  const totalClient = totalNet + vatAmount

  const sendQuote = async () => {
    if (items.length === 0) { alert('Añade al menos un concepto'); return }
    const invalidItems = items.filter(i => i.sale_price < 0)
    if (invalidItems.length > 0) { alert('Los precios no pueden ser negativos'); return }
    setSending(true)
    try {
      await api.post(`/repairs/admin/${repairId}/quote`, {
        items,
        quote_notes: quoteNotes || undefined,
      })
      onSent()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error enviando presupuesto')
    } finally {
      setSending(false)
    }
  }

  const inputClass = "w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:outline-none"

  const catalogItems = items.filter(i => i.item_type === 'catalog_part')
  const customItems = items.filter(i => i.item_type === 'custom_part')
  const shippingItems = items.filter(i => i.item_type === 'shipping')

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 overflow-y-auto py-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-5xl mx-4" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Presupuesto {repair.code}</h2>
            <p className="text-sm text-slate-500">Cliente: {repair.customer_name} ({repair.customer_type})</p>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-5">
          {/* Search Factusol */}
          <div ref={searchRef} className="relative">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5 mb-2">
              <Package className="w-4 h-4" />Buscar en Factusol (productos y servicios)
            </h3>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchParts()}
                  placeholder="Buscar por referencia o nombre..."
                  className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                />
              </div>
              <button onClick={searchParts} disabled={searching || searchQuery.length < 2}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Buscar
              </button>
            </div>
            {showResults && searchResults.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                {searchResults.map((p, i) => (
                  <button key={i} onClick={() => addCatalogPart(p)}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-indigo-50 dark:hover:bg-slate-700/50 flex items-center justify-between border-b last:border-0 border-slate-100 dark:border-slate-700/50 transition-colors">
                    <div className="flex-1 min-w-0 mr-3">
                      <span className="font-mono text-indigo-600 dark:text-indigo-400 text-xs">{p.reference}</span>
                      <span className="text-slate-400 mx-1.5">-</span>
                      <span className="text-slate-700 dark:text-slate-300">{p.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs whitespace-nowrap">
                      <span className="text-slate-400">Coste: {p.cost_price?.toFixed(2)}€</span>
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">PVP: {p.pvp_price?.toFixed(2)}€</span>
                      <span className={`px-1.5 py-0.5 rounded ${p.stock > 0 ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}>
                        Stock: {p.stock}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {showResults && searchResults.length === 0 && !searching && (
              <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl p-4 text-sm text-slate-500 text-center">
                No se encontraron resultados
              </div>
            )}
          </div>

          {/* Items table - Catalog parts */}
          {catalogItems.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Productos / Servicios Factusol</h3>
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-700/50 text-left text-slate-500 text-xs">
                      <th className="px-3 py-2">Referencia</th>
                      <th className="px-3 py-2">Descripción</th>
                      <th className="px-3 py-2 w-16 text-center">Qty</th>
                      <th className="px-3 py-2 w-24 text-right">Coste</th>
                      <th className="px-3 py-2 w-28 text-right">PVP unit.</th>
                      <th className="px-3 py-2 w-24 text-right">Total</th>
                      <th className="px-3 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => item.item_type === 'catalog_part' && (
                      <tr key={idx} className="border-t border-slate-100 dark:border-slate-700/50">
                        <td className="px-3 py-2 font-mono text-xs text-indigo-600 dark:text-indigo-400">{item.factusol_reference}</td>
                        <td className="px-3 py-2 text-slate-900 dark:text-white text-xs">{item.description}</td>
                        <td className="px-3 py-2">
                          <input type="number" min="1" value={item.quantity}
                            onChange={e => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                            className={`${inputClass} text-center w-20`} />
                        </td>
                        <td className="px-3 py-2 text-right text-slate-400 text-xs">{(item.cost_price * item.quantity).toFixed(2)}€</td>
                        <td className="px-3 py-2 text-right">
                          <input type="number" step="0.01" min="0" value={item.sale_price}
                            onChange={e => updateItem(idx, 'sale_price', parseFloat(e.target.value) || 0)}
                            className={`${inputClass} text-right w-24`} />
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900 dark:text-white">{(item.sale_price * item.quantity).toFixed(2)}€</td>
                        <td className="px-3 py-2">
                          <button onClick={() => removeItem(idx)} className="text-slate-400 hover:text-red-500 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Custom parts */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5" />Piezas manuales (excepcionales)
              </h3>
              <button onClick={addCustomPart} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">+ Añadir pieza manual</button>
            </div>
            {customItems.length > 0 && (
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-700/50 text-left text-slate-500 text-xs">
                      <th className="px-3 py-2">Descripción</th>
                      <th className="px-3 py-2 w-24">Proveedor</th>
                      <th className="px-3 py-2 w-20">Ref.</th>
                      <th className="px-3 py-2 w-20 text-center">Qty</th>
                      <th className="px-3 py-2 w-24 text-right">Coste</th>
                      <th className="px-3 py-2 w-28 text-right">PVP unit.</th>
                      <th className="px-3 py-2 w-24 text-right">Total</th>
                      <th className="px-3 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => item.item_type === 'custom_part' && (
                      <tr key={idx} className="border-t border-slate-100 dark:border-slate-700/50">
                        <td className="px-3 py-2">
                          <input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)}
                            placeholder="Nombre pieza" className={inputClass} />
                        </td>
                        <td className="px-3 py-2">
                          <input value={item.supplier_name || ''} onChange={e => updateItem(idx, 'supplier_name', e.target.value)}
                            placeholder="Prov." className={inputClass} />
                        </td>
                        <td className="px-3 py-2">
                          <input value={item.supplier_reference || ''} onChange={e => updateItem(idx, 'supplier_reference', e.target.value)}
                            placeholder="Ref." className={inputClass} />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" min="1" value={item.quantity}
                            onChange={e => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                            className={`${inputClass} text-center w-20`} />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" step="0.01" min="0" value={item.cost_price}
                            onChange={e => updateItem(idx, 'cost_price', parseFloat(e.target.value) || 0)}
                            className={`${inputClass} text-right w-24`} />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" step="0.01" min="0" value={item.sale_price}
                            onChange={e => updateItem(idx, 'sale_price', parseFloat(e.target.value) || 0)}
                            className={`${inputClass} text-right w-24`} />
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900 dark:text-white">{(item.sale_price * item.quantity).toFixed(2)}€</td>
                        <td className="px-3 py-2">
                          <button onClick={() => removeItem(idx)} className="text-slate-400 hover:text-red-500 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Shipping - solo para recogida a domicilio */}
          {repair.shipping_option !== 'in_store' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Truck className="w-3.5 h-3.5" />Transporte
              </h3>
              {!items.some(i => i.item_type === 'shipping') && (
                <button onClick={addShipping} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">+ Añadir transporte</button>
              )}
            </div>
            {shippingItems.length > 0 && (
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-700/50 text-left text-slate-500 text-xs">
                      <th className="px-3 py-2">Concepto</th>
                      <th className="px-3 py-2 w-24 text-right">Coste</th>
                      <th className="px-3 py-2 w-28 text-right">Precio venta</th>
                      <th className="px-3 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => item.item_type === 'shipping' && (
                      <tr key={idx} className="border-t border-slate-100 dark:border-slate-700/50">
                        <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{item.description}</td>
                        <td className="px-3 py-2 text-right text-slate-400 text-xs">{item.cost_price.toFixed(2)}€</td>
                        <td className="px-3 py-2 text-right">
                          <input type="number" step="0.01" min="0" value={item.sale_price}
                            onChange={e => updateItem(idx, 'sale_price', parseFloat(e.target.value) || 0)}
                            className={`${inputClass} text-right w-24`} />
                        </td>
                        <td className="px-3 py-2">
                          <button onClick={() => removeItem(items.indexOf(item))} className="text-slate-400 hover:text-red-500 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}

          {/* Summary */}
          <div className="bg-slate-50 dark:bg-slate-700/30 rounded-xl p-5 border border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Resumen del presupuesto</h3>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-500"><span>Coste total</span><span>{totalCost.toFixed(2)} €</span></div>
              <div className="flex justify-between"><span className="text-slate-700 dark:text-slate-300">Venta neta (sin IVA)</span><span className="font-medium">{totalNet.toFixed(2)} €</span></div>
              <div className="flex justify-between text-slate-500">
                <span>Margen</span>
                <span className={marginAmount >= 0 ? 'text-emerald-600' : 'text-red-500'}>
                  {marginAmount >= 0 ? '+' : ''}{marginAmount.toFixed(2)} € ({marginPct.toFixed(1)}%)
                </span>
              </div>
              <div className="border-t border-slate-200 dark:border-slate-600 pt-1.5 flex justify-between text-slate-500">
                <span>IVA ({vatRate}%)</span><span>+{vatAmount.toFixed(2)} €</span>
              </div>
              <div className="border-t-2 border-slate-300 dark:border-slate-600 pt-2 flex justify-between text-lg font-bold">
                <span className="text-slate-900 dark:text-white">TOTAL CLIENTE</span>
                <span className="text-indigo-600 dark:text-indigo-400">{totalClient.toFixed(2)} €</span>
              </div>
            </div>
            {marginAmount < 0 && (
              <div className="mt-3 flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>El margen es negativo. Revisa los precios de venta.</span>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notas para el cliente</label>
            <textarea value={quoteNotes} onChange={e => setQuoteNotes(e.target.value)} rows={2} placeholder="Información adicional..."
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:outline-none" />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">Cancelar</button>
          <button onClick={sendQuote} disabled={sending || items.length === 0}
            className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Enviar presupuesto al cliente
          </button>
        </div>
      </div>
    </div>
  )
}
